<!--
/**
 * DjQueryTab.vue - 查询 Tab 组件
 *
 * 【功能说明】
 * 本组件是迪加(DJ)虚拟商品管理系统的号码查询页面，用于查询单个号码的详细信息。
 * 输入手机号后，可以查看该号码的账户信息、账户状态、话费余量、
 * 语音/数据/短信/彩信用量、停复机记录、充值历史等全方位信息。
 *
 * 【主要功能】
 * 1. 号码搜索 - 根据手机号查询号码的全部信息
 * 2. 地址信息展示 - 显示激活地址、首通地址、漫游地址
 * 3. 账户信息展示 - 显示号码、地市、网络、限频等基础信息
 * 4. 账户状态展示 - 显示用户状态、套餐名称、激活时间
 * 5. 话费余量展示 - 显示当月消费、欠费金额、话费余额
 * 6. 用量信息展示 - 展示语音、数据、短信、彩信的总量/用量/剩余量
 * 7. 停复机记录 - 展示该号码的停机/复机历史记录表格
 * 8. 充值历史记录 - 展示该号码的充值历史（仅管理员可见）
 * 9. 记录复机功能 - 对已停机的号码设置自动复机（记录复机时间）
 *
 * 【权限要求】
 * - 所有用户：可搜索号码、查看基本信息和停复机记录
 * - 管理员 (isAdmin)：额外可查看充值历史、查看原始套餐名称
 -->
<template>
  <div>
    <!-- ========== 搜索区域 ========== -->
    <div class="handle-box">
      <!-- 号码输入框，支持清除，最大长度12位 -->
      <el-input size="small" v-model="phone" clearable maxlength="12" placeholder="号码"
                style="width: 180px;" class="handle-input mr10"></el-input>
      <!-- 搜索按钮，点击调用 dxtQuerys 方法查询号码信息 -->
      <el-button size="small" type="primary" :class="[theme]" icon="el-icon-search"
                 @click="dxtQuerys">搜索
      </el-button>
    </div>

    <!-- ========== 地址信息区域（查询到数据后才显示） ========== -->
    <div v-if="dxtDet.BILL_ID" class="region_box">
      <!-- 激活地址 - 号码首次激活时的所在地 -->
      <div class="region_list">
        <div class="region_list_tit">激活地址：</div>
        <div class="region_list_info">{{ dxtDet.ACTIVE_REGION }}</div>
      </div>
      <!-- 首通地址 - 号码首次接通电话时的所在地 -->
      <div class="region_list">
        <div class="region_list_tit">首通地址：</div>
        <div class="region_list_info">{{ dxtDet.FIRST_REGION }}</div>
      </div>
      <!-- 漫游地址 - 号码当前漫游所在的地区 -->
      <div class="region_list">
        <div class="region_list_tit">漫游地址：</div>
        <div class="region_list_info">{{ dxtDet.ROAM_REGION }}</div>
      </div>
    </div>

    <!-- ========== 账户信息 + 账户状态 + 话费余量 ========== -->
    <div class="table-box" v-if="dxtDet.BILL_ID">
      <!-- 账户基础信息卡片 -->
      <div class="user-info-box" style="width: 300px;">
        <div class="user-info-tit">账户信息</div>
        <div class="info-box">
          <div class="info-name">号码：</div>
          <div class="info">{{ dxtDet.BILL_ID }}</div>
        </div>
        <div class="info-box">
          <div class="info-name">地市：</div>
          <div class="info">{{ dxtDet.DISTRICT_NAME }}</div>
        </div>
        <div class="info-box">
          <div class="info-name">网络：</div>
          <div class="info">{{ dxtDet.NET }}</div>
        </div>
        <div class="info-box">
          <div class="info-name">限频：</div>
          <div class="info">{{ dxtDet.VOICE_MONITOR_NUMBER || 0 }}</div>
        </div>
      </div>

      <!-- 账户状态信息卡片 -->
      <div class="user-info-box">
        <div class="user-info-tit">账户状态</div>
        <div class="info-box">
          <div class="info-name">用户状态：</div>
          <div class="info">{{ dxtDet.STATE }}
            <!-- 如果有状态原因则显示在括号中 -->
            <span v-if="dxtDet.QT_CAUSE">（{{ dxtDet.QT_CAUSE }}）</span>
          </div>
        </div>
        <div class="info-box">
          <div class="info-name">套餐名称：</div>
          <div class="info">
            <!-- 管理员可看到原始套餐名称（悬浮显示） -->
            <template v-if="isAdmin && dxtDet.OLD_ORDER_NAME">
              <el-popover trigger="hover" placement="top">
                <div style="font-size: 13px;">{{ dxtDet.OLD_ORDER_NAME }}</div>
                <template #reference>
                  <span style="cursor: pointer; color: #409eff;">{{ dxtDet.ORDER_NAME }}</span>
                </template>
              </el-popover>
            </template>
            <template v-else>
              {{ dxtDet.ORDER_NAME }}
            </template>
          </div>
        </div>
        <div class="info-box">
          <div class="info-name">激活时间：</div>
          <div class="info">{{ dxtDet.ACTIVE_DATE }}</div>
        </div>
        <div class="info-box">
          <div class="info-name"></div>
          <div class="info"></div>
        </div>
      </div>

      <!-- 话费余量信息卡片 -->
      <div class="user-info-box" style="width: 300px;">
        <div class="user-info-tit">话费余量</div>
        <div class="info-box">
          <div class="info-name">当月消费：</div>
          <div class="info">{{ dxtDet.BALANCE }}元</div>
        </div>
        <div class="info-box">
          <div class="info-name">欠费金额：</div>
          <!-- 欠费金额用红色标注 -->
          <div class="info" style="color: #E81A1A;">{{ dxtDet.AMOUNT_OWED }}元</div>
        </div>
        <div class="info-box">
          <div class="info-name">话费余额：</div>
          <!-- 话费余额用深红色标注 -->
          <div class="info" style="color: #B12E2E;">{{ dxtDet.REAL_BALANCE }}元</div>
        </div>
        <div class="info-box">
          <div class="info-name"></div>
          <div class="info"></div>
        </div>
      </div>
    </div>

    <!-- ========== 语音/数据/短信/彩信用量信息（dosageVO有数据时显示） ========== -->
    <div class="table-box" v-if="dosageVO">
      <!-- 语音信息卡片 - 展示语音通话的总量/已用/剩余（单位：分钟） -->
      <div class="user-info-box" style="width: 300px;">
        <div class="user-info-tit">语音信息</div>
        <div class="info-box">
          <div class="info-name">语音总量</div>
          <div class="info">{{ dosageVO.yyTotal }}分钟</div>
        </div>
        <div class="info-box">
          <div class="info-name">语音用量</div>
          <div class="info">{{ dosageVO.yyUse }}分钟</div>
        </div>
        <div class="info-box">
          <div class="info-name">语音剩余量</div>
          <div class="info">{{ dosageVO.yySurplus }}分钟</div>
        </div>
      </div>
      <!-- 数据信息卡片 - 展示流量数据的总量/已用/剩余（单位：KB） -->
      <div class="user-info-box" style="width: 300px;">
        <div class="user-info-tit">数据信息</div>
        <div class="info-box">
          <div class="info-name">数据总量</div>
          <div class="info">{{ dosageVO.sjTotal }}KB</div>
        </div>
        <div class="info-box">
          <div class="info-name">数据用量</div>
          <div class="info">{{ dosageVO.sjUse }}KB</div>
        </div>
        <div class="info-box">
          <div class="info-name">数据剩余量</div>
          <div class="info">{{ dosageVO.sjSurplus }}KB</div>
        </div>
      </div>
      <!-- 短信信息卡片 - 展示短信的总量/已用/剩余（单位：条） -->
      <div class="user-info-box" style="width: 300px;">
        <div class="user-info-tit">短信信息</div>
        <div class="info-box">
          <div class="info-name">短信总量</div>
          <div class="info">{{ dosageVO.dxTotal }}条</div>
        </div>
        <div class="info-box">
          <div class="info-name">短信用量</div>
          <div class="info">{{ dosageVO.dxUse }}条</div>
        </div>
        <div class="info-box">
          <div class="info-name">短信剩余量</div>
          <div class="info">{{ dosageVO.dxSurplus }}条</div>
        </div>
      </div>
      <!-- 彩信信息卡片 - 展示彩信的总量/已用/剩余（单位：条） -->
      <div class="user-info-box" style="width: 300px;">
        <div class="user-info-tit">彩信信息</div>
        <div class="info-box">
          <div class="info-name">彩信总量</div>
          <div class="info">{{ dosageVO.cxTotal }}条</div>
        </div>
        <div class="info-box">
          <div class="info-name">彩信用量</div>
          <div class="info">{{ dosageVO.cxUse }}条</div>
        </div>
        <div class="info-box">
          <div class="info-name">彩信剩余量</div>
          <div class="info">{{ dosageVO.cxSurplus }}条</div>
        </div>
      </div>
    </div>

    <!-- ========== 充值历史表格（仅管理员可见） ========== -->
    <div class="table-box" v-if="dxtDet.BILL_ID && isAdmin">
      <el-table :data="rechargeHistoryData" border class="table" max-height="400"
                header-cell-class-name="table-header" :row-style="{ height: '20px' }"
                :cell-style="{ padding: '6px' }" :header-cell-style="{ 'text-align': 'center' }" stripe
                v-loading="rechargeHistoryLoading">
        <!-- 充值月份列 -->
        <el-table-column prop="year_month" label="充值月份" align="center" width="120"></el-table-column>
        <!-- 套餐名称列（优先显示sjtxPackagesName，否则显示packagesName） -->
        <el-table-column label="套餐名称" align="center" show-overflow-tooltip width="200">
          <template #default="scope">
            {{ scope.row.sjtxPackagesName || scope.row.packagesName }}
          </template>
        </el-table-column>
        <!-- 用户充值金额列 -->
        <el-table-column prop="rechargeAmount" label="用户充值金额" align="center" width="120">
          <template #default="scope">
            <span>{{ scope.row.rechargeAmount }}元</span>
          </template>
        </el-table-column>
        <!-- 立即到账金额列（优先显示immediateArrivalAmount，否则显示arrivalAmount） -->
        <el-table-column label="立即到账金额" align="center" width="110">
          <template #default="scope">
            <span v-if="scope.row.immediateArrivalAmount">{{ scope.row.immediateArrivalAmount.toFixed(2) }}元</span>
            <span v-else-if="scope.row.arrivalAmount">{{ scope.row.arrivalAmount.toFixed(2) }}元</span>
            <span v-else>0.00元</span>
          </template>
        </el-table-column>
        <!-- 次月到账金额列（根据处理状态显示不同颜色：绿色=已到账，橙色=待到账） -->
        <el-table-column label="次月到账金额" align="center" width="110">
          <template #default="scope">
            <span v-if="scope.row.nextMonthProcessed && scope.row.nextMonthArrivalAmount" :style="{ color: '#67c23a', fontWeight: 'bold' }">
              {{ (scope.row.nextMonthActualArrivalAmount || scope.row.nextMonthArrivalAmount).toFixed(2) }}元
            </span>
            <span v-else-if="scope.row.nextMonthArrivalAmount" :style="{ color: '#e6a23c', fontWeight: 'bold' }">
              待到账{{ scope.row.nextMonthArrivalAmount.toFixed(2) }}元
            </span>
            <span v-else-if="scope.row.normalNextMonthArrival && scope.row.status == 1" :style="{ color: '#67c23a', fontWeight: 'bold' }">
              {{ scope.row.normalNextMonthArrival.toFixed(2) }}元
            </span>
            <span v-else-if="scope.row.normalNextMonthArrival" :style="{ color: '#e6a23c', fontWeight: 'bold' }">
              待到账{{ scope.row.normalNextMonthArrival.toFixed(2) }}元
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <!-- 服务费列（红色显示拦截金额） -->
        <el-table-column label="服务费" align="center" width="110">
          <template #default="scope">
            <span v-if="scope.row.needIntercept" :style="{ color: '#f56c6c' }">{{ scope.row.needIntercept.toFixed(2) }}元</span>
            <span v-else>0.00元</span>
          </template>
        </el-table-column>
        <!-- 处理状态列（使用标签样式区分：成功=绿色，待处理=橙色） -->
        <el-table-column prop="status" label="处理状态" align="center" width="90">
          <template #default="scope">
            <el-tag v-if="scope.row.nextMonthProcessed" type="success" size="mini">次月已处理</el-tag>
            <el-tag v-else-if="scope.row.status == 0" type="warning" size="mini">待处理</el-tag>
            <el-tag v-else-if="scope.row.status == 1" type="success" size="mini">已处理</el-tag>
            <el-tag v-else type="info" size="mini">未知</el-tag>
          </template>
        </el-table-column>
        <!-- 批次号列（双击可复制） -->
        <el-table-column prop="fileBatchNo" label="批次号" align="center" width="150">
          <template #default="scope">
            <span @dblclick="copyBatchNo(scope.row.fileBatchNo)" style="pointer-events: auto; cursor: pointer;" :title="scope.row.fileBatchNo || ''">
              {{ scope.row.fileBatchNo || '-' }}
            </span>
          </template>
        </el-table-column>
        <!-- 创建时间列 -->
        <el-table-column prop="createdAt" label="创建时间" align="center" width="180">
          <template #default="scope">
            <span>{{ formatDateTime(scope.row.createdAt) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- ========== 停复机记录表格 ========== -->
    <el-table :data="tableData3" border class="table" ref="multipleTable" max-height="600"
              header-cell-class-name="table-header" :row-style="{ height: '20px' }"
              :cell-style="{ padding: '6px' }" :header-cell-style="{ 'text-align': 'center' }" stripe
              v-loading="loading">
      <!-- 代理商列 -->
      <el-table-column prop="agent" label="代理商" align="center" show-overflow-tooltip></el-table-column>
      <!-- 企业名称列 -->
      <el-table-column prop="company" label="企业名称" align="center" show-overflow-tooltip></el-table-column>
      <!-- 手机号列 -->
      <el-table-column prop="phone" label="手机号" width="130" align="center"></el-table-column>
      <!-- 状态列（提交失败时红色显示，并悬浮提示错误原因） -->
      <el-table-column prop="status" label="状态" width="90" align="center">
        <template #default="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.errMsg" placement="top"
                      v-if="scope.row.status == '提交失败'">
            <span style="color: #f56c6c;">{{ scope.row.status }}</span>
          </el-tooltip>
          <span v-else>{{ scope.row.status }}</span>
        </template>
      </el-table-column>
      <!-- 类型列（停机=红色背景，复机=绿色背景） -->
      <el-table-column prop="type" label="类型" align="center" width="120">
        <template #default="scope">
          <span v-if="scope.row.type == '停机'" class="status_color2 bgred">{{ scope.row.type }}</span>
          <span v-if="scope.row.type == '复机'" class="status_color2 bggreen">{{ scope.row.type }}</span>
        </template>
      </el-table-column>
      <!-- 原因列 -->
      <el-table-column prop="operateMsg" label="原因" align="center" show-overflow-tooltip></el-table-column>
      <!-- 记录时间列 -->
      <el-table-column prop="createdAt" label="记录时间" align="center" width="155"></el-table-column>
      <!-- 复机操作列 - 点击"记录复机"打开自动复机设置弹窗 -->
      <el-table-column label="复机" align="center" width="100">
        <template #default="scope">
          <span @click="getRecordVisible(scope.row)" style="cursor: pointer;color: #ff0000;">记录复机</span>
        </template>
      </el-table-column>
      <!-- 操作者列 -->
      <el-table-column prop="userPhone" label="操作者" align="center" width="130"></el-table-column>
    </el-table>

    <!-- ========== 自动复机设置弹窗 ========== -->
    <!-- 用于对停机记录设置自动复机，包括：是否记录、是否复机、复机延迟时间 -->
    <el-dialog title="自动复机设置" v-model="repeatedVisible" width="520px" @close="closeRepeatedDialog">
      <el-form label-width="120px" :model="repeatedForm" ref="repeatedForm">
        <!-- 是否记录 - 控制是否将该停机记录标记为需要自动复机 -->
        <el-form-item label="是否记录" prop="record">
          <el-radio-group v-model="repeatedForm.record">
            <el-radio :label="true">是</el-radio>
            <el-radio :label="false">否</el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 是否复机 - 显示状态（禁用，只读展示） -->
        <el-form-item label="是否复机" prop="recover">
          <el-radio-group v-model="repeatedForm.recover" disabled>
            <el-radio :label="true">是</el-radio>
            <el-radio :label="false">否</el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 复机时间 - 选择延迟多少小时后自动复机（仅在"是否记录"为"是"时显示） -->
        <el-form-item label="复机时间" prop="hours" v-if="repeatedForm.record">
          <el-radio-group v-model="repeatedForm.hours">
            <el-radio v-for="item in hoursList" :key="item.value" :label="item.value" :value="item.value"
                      style="margin: 12px 52px 12px 0;">{{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 记录时间 - 显示该记录的最后更新时间（禁用，只读） -->
        <el-form-item label="记录时间" prop="updatedAt">
          <el-input size="small" v-model="repeatedForm.updatedAt" disabled></el-input>
        </el-form-item>
        <!-- 弹窗底部按钮 -->
        <el-form-item class="dis-end">
          <el-button size="small" @click="closeRepeatedDialog">取消</el-button>
          <el-button size="small" type="primary" :class="[theme]" @click="getRecordingRecover">确定</el-button>
        </el-form-item>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 查询 Tab 组件脚本部分
 *
 * 引入的 API 接口：
 * - dxtQuery：号码查询接口，根据手机号查询号码全部信息
 * - stopOrOpenRecord：停复机记录查询接口
 * - getPackageTopUpRecord：充值历史记录查询接口
 * - getDxtPackagesOfNumber：获取套餐列表接口（用于匹配套餐名称）
 * - dxtRecordingRecover：记录复机接口（设置自动复机）
 */
import {
  dxtQuery,
  stopOrOpenRecord,
  getPackageTopUpRecord,
  getDxtPackagesOfNumber,
  dxtRecordingRecover
} from '../../../api/dijia.js';

export default {
  name: 'DjQueryTab',

  /**
   * 【Props 接收的参数】
   * - theme：主题颜色样式类名，用于按钮等元素的主题色
   * - theme3：辅助主题颜色类名
   * - isAdmin：是否为管理员，控制充值历史等敏感信息的显示
   * - token：请求头信息（包含认证令牌），用于文件上传时的鉴权
   */
  props: {
    theme: { type: String, default: '' },
    theme3: { type: String, default: '' },
    isAdmin: { type: Boolean, default: false },
    token: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      // ========== 查询相关 ==========
      loading: false,           // 表格加载状态
      phone: localStorage.getItem('userinfophone') || '',  // 搜索的手机号（默认读取缓存中的号码）

      // ========== 号码详情信息 ==========
      dxtDet: '',               // 号码详细信息对象（含账户信息、状态、话费余量等）
      dosageVO: '',             // 用量信息对象（语音、数据、短信、彩信的总量/用量/剩余）

      // ========== 停复机记录表格 ==========
      tableData3: [],           // 停复机记录列表数据

      // ========== 充值历史相关（仅管理员） ==========
      rechargeHistoryData: [],  // 充值历史记录列表
      rechargeHistoryLoading: false,  // 充值历史表格加载状态
      rechargeHistoryTotal: 0,        // 充值历史总条数
      packagesList: [],               // 套餐名称映射列表（用于将packagesName转换为sjtxPackagesName）

      // ========== 自动复机弹窗相关 ==========
      repeatedVisible: false,   // 自动复机弹窗是否显示
      repeatedForm: {           // 自动复机表单数据
        record: false,          // 是否记录（标记为需要自动复机）
        recover: false,         // 是否复机（只读状态）
        updatedAt: '',          // 记录更新时间（只读）
        hours: '',              // 复机延迟小时数
      },
      hoursList: [              // 可选的复机延迟时间列表
        { value: 2, label: '2小时' },
        { value: 4, label: '4小时' },
        { value: 6, label: '6小时' },
        { value: 12, label: '12小时' },
        { value: 24, label: '24小时' }
      ],
    };
  },

  /**
   * 组件创建时的生命周期钩子
   * 1. 获取套餐名称映射列表
   * 2. 如果缓存中有手机号，自动查询该号码
   */
  created() {
    this.getPackagesList();
    if (this.phone) {
      this.dxtQuerys();
    }
  },

  methods: {
    /**
     * getPackagesList - 获取套餐名称映射列表
     * 用于将充值历史中的 packagesName 转换为 sjtxPackagesName（数据对接套餐名）
     * 获取后如果有充值历史数据，则重新处理套餐名称映射
     */
    getPackagesList() {
      getDxtPackagesOfNumber().then(res => {
        if (res?.code === 200) {
          this.packagesList = res.data;
          // 如果已有充值历史数据，重新处理套餐名称映射
          if (this.rechargeHistoryData && this.rechargeHistoryData.length > 0) {
            this.processRechargeHistoryData();
          }
        }
      });
    },

    /**
     * dxtQuerys - 搜索号码主方法
     * 清空之前的查询结果，调用号码查询API获取号码详情信息
     * 查询成功后自动加载停复机记录，管理员还会加载充值历史
     */
    dxtQuerys() {
      // 清空之前的查询结果
      this.dxtDet = '';
      this.dosageVO = '';
      this.tableData3 = [];
      this.rechargeHistoryData = [];
      this.rechargeHistoryTotal = 0;
      // 将号码缓存到 localStorage
      localStorage.setItem('userinfophone', this.phone);
      let data = { phone: this.phone };
      dxtQuery(data).then(res => {
        if (res?.code === 200) {
          this.dxtDet = res.data;
          // 解析用量信息（JSON字符串转对象）
          this.dosageVO = JSON.parse(this.dxtDet.dosageVO);
          // 查询停复机记录
          this.getstopOrOpenRecord2();
          // 管理员额外查询充值历史
          if (this.isAdmin) {
            this.getRechargeHistory();
          }
        } else {
          this.$message.error(res.message);
        }
      });
    },

    /**
     * getRechargeHistory - 获取该号码的充值历史记录（仅管理员调用）
     * 查询后过滤掉已退款的记录，并处理套餐名称映射
     */
    getRechargeHistory() {
      if (!this.phone) return;
      this.rechargeHistoryLoading = true;
      getPackageTopUpRecord({
        phone: this.phone,
        pageIndex: 1,
        pageSize: 100
      }).then(res => {
        this.rechargeHistoryLoading = false;
        if (res?.code === 200) {
          // 过滤掉已退款的记录
          this.rechargeHistoryData = res.data.list.filter(record => !record.refund);
          this.rechargeHistoryTotal = this.rechargeHistoryData.length;
          // 处理套餐名称映射
          this.processRechargeHistoryData();
        } else {
          this.rechargeHistoryData = [];
          this.rechargeHistoryTotal = 0;
        }
      }).catch(() => {
        this.rechargeHistoryLoading = false;
      });
    },

    /**
     * processRechargeHistoryData - 处理充值历史数据中的套餐名称映射
     * 将每条充值记录的 packagesName 匹配到套餐列表中的 sjtxPackagesName
     */
    processRechargeHistoryData() {
      if (!this.packagesList || this.packagesList.length === 0) return;
      this.rechargeHistoryData.forEach(record => {
        const foundPackage = this.packagesList.find(p => p.packagesName === record.packagesName);
        if (foundPackage) {
          record.sjtxPackagesName = foundPackage.sjtxPackagesName;
        }
      });
    },

    /**
     * getstopOrOpenRecord2 - 获取当前号码的停复机记录
     * 查询该手机号的所有停复机操作记录（最多99条）
     */
    getstopOrOpenRecord2() {
      let data = {
        userId: localStorage.getItem('user'),
        phone: this.phone,
        pageIndex: 1,
        pageSize: 99,
      };
      this.loading = true;
      stopOrOpenRecord(data).then(res => {
        this.loading = false;
        if (res?.code === 200) {
          this.tableData3 = res.data.list;
        } else {
          this.$message.error(res.message);
        }
      });
    },

    /**
     * getRecordVisible - 打开自动复机设置弹窗
     * 将当前行的复机设置数据填充到弹窗表单中
     * @param {Object} row - 停复机记录行数据
     */
    getRecordVisible(row) {
      this.repeatedVisible = true;
      this.repeatedForm.record = row.record;
      this.repeatedForm.recover = row.recover;
      this.repeatedForm.id = row.id;
      this.repeatedForm.hours = row.hours;
      this.repeatedForm.updatedAt = row.updatedAt;
    },

    /**
     * closeRepeatedDialog - 关闭自动复机设置弹窗
     */
    closeRepeatedDialog() {
      this.repeatedVisible = false;
    },

    /**
     * getRecordingRecover - 提交自动复机设置
     * 将是否记录、复机延迟时间等参数提交到后端保存
     * 成功后关闭弹窗并刷新停复机记录
     */
    getRecordingRecover() {
      let data = {
        id: this.repeatedForm.id,
        record: this.repeatedForm.record,
        hours: this.repeatedForm.hours
      };
      dxtRecordingRecover(data).then(res => {
        if (res?.code === 200) {
          this.repeatedVisible = false;
          this.$message.success(res.data);
          // 刷新停复机记录列表
          this.getstopOrOpenRecord2();
        } else {
          this.$message.error(res.message);
        }
      });
    },

    /**
     * copyBatchNo - 复制批次号到剪贴板
     * 优先使用 Clipboard API，不支持时回退到 execCommand('copy')
     * @param {string} batchNo - 要复制的批次号
     */
    copyBatchNo(batchNo) {
      if (!batchNo) {
        this.$message.warning('批次号为空');
        return;
      }
      // 优先使用现代 Clipboard API
      navigator.clipboard.writeText(batchNo).then(() => {
        this.$message.success('批次号已复制');
      }).catch(() => {
        // 回退方案：创建临时 textarea 元素进行复制
        const textarea = document.createElement('textarea');
        textarea.value = batchNo;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          this.$message.success('批次号已复制');
        } catch (e) {
          this.$message.error('复制失败，请手动复制');
        }
        document.body.removeChild(textarea);
      });
    },

    /**
     * formatDateTime - 格式化日期时间
     * 将日期对象或时间戳格式化为 "YYYY-MM-DD HH:mm:ss" 字符串
     * @param {Date|string|number} dateTime - 日期时间对象或时间戳
     * @returns {string} 格式化后的时间字符串，无数据时返回 '-'
     */
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  }
};
</script>

<style scoped>
@import './tab-styles.css';
</style>
